import {
  Breadcrumbs,
  Button,
  Icon,
  LinkButton,
  TextFading
} from '@yes.technology/react-toolkit'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import GenericObjectCardButton from 'component/GenericObjectCard/GenericObjectCardButton'
import SitemodelModal from 'component/SitemodelModal/SitemodelModal'
import { useObjectclass } from 'objectclass/shared'
import { useLocation } from 'react-router'

import useTaskStatusActions from './hooks/useTaskStatusActions/useTaskStatusActions'
import { TaskActionsContainer, TaskActionsRow } from './TaskObjectCard.styles'
import { TaskActionsProps } from './TaskObjectCard.types'

const TaskActions = ({
  statusUuid,
  taskUuid,
  statusActions,
  classification,
  changeStatus,
  objectclassUuid,
  des,
  returnOnFinish = false,
  buttonProps,
  visualizeUrl = ''
}: TaskActionsProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { objectclass } = useObjectclass({ objectclassUuid })

  const handleClose = () => setIsModalOpen(false)

  const visualizeBreadcrumbRoutes = useMemo(
    () => [
      { description: objectclass?.des ?? '', url: '' },
      { description: des, url: '' }
    ],
    [objectclass?.des, des]
  )

  const relativeEncodedUrl = encodeURIComponent(
    location.pathname + location.search + location.hash
  )
  const executeHref = `/${window.taskExecutorSitemodelUuid}?taskUuid=${taskUuid}&returnUrl=${relativeEncodedUrl}&returnOnFinish=${returnOnFinish.toString()}`

  const visualizeDestinationUrl = visualizeUrl
    ? `${visualizeUrl}?uuid_objectclass=${objectclassUuid}&object-uuid=${taskUuid}`
    : `/${window.objectDetailsSitemodelUuid}?uuid_objectclass=${objectclassUuid}&object-uuid=${taskUuid}`

  const isWaitingInteraction =
    window.waitingInteractionStatusUuid === statusUuid

  const { displayedStatusActions, showOnlyVisualizeButton } =
    useTaskStatusActions(statusActions, classification)

  const visualizeButton = (
    <Button
      key='view'
      variant='secondary'
      heightClass='small'
      style={{ width: '24px', minWidth: '24px' }}
      aria-label={t('task.action.view')}
      onClick={() => setIsModalOpen(true)}
    >
      <Icon iconName='Visualization' icSize='small' />
    </Button>
  )

  const executeButton =
    buttonProps !== undefined ? (
      <GenericObjectCardButton
        buttonProps={buttonProps}
        href={executeHref}
        label={t('task.action.execute')}
        variant='primary'
      />
    ) : (
      <LinkButton
        rel='noopener noreferrer'
        variant='primary'
        heightClass='small'
        href={executeHref}
      >
        {t('task.action.execute')}
      </LinkButton>
    )

  if (showOnlyVisualizeButton && !isWaitingInteraction) {
    return (
      <TaskActionsContainer>
        <Button
          key='view'
          variant='secondary'
          heightClass='small'
          style={{ width: '100%' }}
          aria-label={t('task.action.view')}
          onClick={() => setIsModalOpen(true)}
        >
          <Icon iconName='Visualization' icSize='small' />
        </Button>
        {isModalOpen && (
          <SitemodelModal
            title={
              <TextFading>
                <Breadcrumbs
                  color='black'
                  boldLastItem={false}
                  spaceBetween='8px'
                  fontStyle='italic'
                  heightClass='large'
                  separator='|'
                  routes={visualizeBreadcrumbRoutes}
                />
              </TextFading>
            }
            completeURL={visualizeDestinationUrl}
            onClose={handleClose}
          />
        )}
      </TaskActionsContainer>
    )
  }

  return (
    <TaskActionsContainer>
      {isWaitingInteraction && (
        <TaskActionsRow>
          {executeButton}
          {!displayedStatusActions.length && visualizeButton}
        </TaskActionsRow>
      )}
      {displayedStatusActions.map((statusAction, i) => (
        <TaskActionsRow
          key={`${statusAction.destination_status[0].code}_${i}`}
          $fullWidth={i === displayedStatusActions.length - 1}
        >
          <Button
            variant={!isWaitingInteraction && i === 0 ? 'primary' : 'secondary'}
            heightClass='small'
            onClick={() =>
              changeStatus(statusAction.destination_status[0].code)
            }
          >
            {statusAction.des}
          </Button>
          {i === displayedStatusActions.length - 1 && visualizeButton}
        </TaskActionsRow>
      ))}
      {isModalOpen && (
        <SitemodelModal
          title={
            <TextFading>
              <Breadcrumbs
                color='black'
                boldLastItem={false}
                spaceBetween='8px'
                fontStyle='italic'
                heightClass='large'
                separator='|'
                routes={visualizeBreadcrumbRoutes}
              />
            </TextFading>
          }
          completeURL={visualizeDestinationUrl}
          onClose={handleClose}
        />
      )}
    </TaskActionsContainer>
  )
}

export default TaskActions
